'use client';
import { Image } from '../../core/ui';
import './SortableImageList.css';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	MouseSensor,
	TouchSensor,
	useSensor,
	useSensors,
	DragOverlay,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { DraggableImage, OverlayImage } from '../DraggableImage/DraggableImage';
import { ImageBox } from '../ImageBox/ImageBox';
import { ReactNode, useState } from 'react';

interface SortableImageListProps {
	images: Image[];
	className?: string;
	onEditImage?: (image: Image) => void;
	onRemoveImage: (position: number) => void;
	onSortImage: (overID: number, movedId: number) => void;
	onSeeFullscreenImage?: (position: number | null) => void;
	dropZone: ReactNode;
	loadingImages?: ReactNode;
	t?: any;
}

const SortableImageList = ({
	images,
	className,
	onEditImage,
	onRemoveImage,
	onSortImage,
	onSeeFullscreenImage,
	dropZone,
	loadingImages,
	t,
}: SortableImageListProps) => {
	const [draggedImage, setDraggedImage] = useState<number>();

	const sensors = useSensors(
		useSensor(MouseSensor),
		useSensor(TouchSensor, {
			activationConstraint: {
				delay: 100,
				tolerance: 5,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const onDragEnd = (e: any) => {
		const { over, active } = e;
		if (over !== active) {
			onSortImage(over.id, active.id);
		}
		setDraggedImage(undefined);
	};

	const onDragStart = (e: any) => {
		const { active } = e;
		setDraggedImage(active.id);
	};

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			modifiers={[restrictToParentElement]}
			onDragStart={onDragStart}
			onDragEnd={onDragEnd}
		>
			<SortableContext items={images.map((image) => image.position!)}>
				<div className={`sortable-image-list${className ? ' ' + className : ''}`}>
					{images
						.sort((a, b) => (a.position! < b.position! ? -1 : 1))
						.map((image) => (
							<DraggableImage position={image.position!} key={image.position}>
								<ImageBox
									url={image.url}
									position={image.position}
									onEditImage={
										onEditImage != null && image.file != null
											? () => onEditImage(image)
											: undefined
									}
									onRemoveImage={() => onRemoveImage(image.position!)}
									onSeeFullscreenImage={
										onSeeFullscreenImage
											? () => onSeeFullscreenImage(image.position)
											: undefined
									}
									isDraggable={true}
									t={t}
								/>
							</DraggableImage>
						))}
					{loadingImages}
					{dropZone}
				</div>
			</SortableContext>
			{draggedImage && (
				<DragOverlay>
					<OverlayImage url={images.find((s) => s.position === draggedImage)!.url} />
				</DragOverlay>
			)}
		</DndContext>
	);
};

export { SortableImageList };
